import type { RootState } from '@restore/store';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';
import { getAllVisitLabels, makeAllConfigLabelsFlat } from '../../transformers';
import { createSelector } from '@reduxjs/toolkit';
import type { ISTasksContextResponse, OverviewUpload } from '@api/isTasks';
import { LabelMerged } from '@dynamika-shared/types/imagingLabels';
import {
  DICOM_Series,
  QCTaskPatientVisit,
  QCVisitWithSeries,
  ViewerVisit,
} from '@dynamika-shared/types/visits';
import { Context } from '@dynamika-shared/types/clinicalTrialContext';
import { StudyType } from '@dynamika-shared/services/communication';

const selectAuthRoles = (state: RootState): string[] =>
  state.auth.data?.['dynamika7/user_metadata']?.roles ?? [];

const selectSimpleViewerData = (
  state: RootState
): ISTasksContextResponse | null => state.SimpleViewer?.data ?? null;

const selectSimpleViewerDicomResource = (state: RootState): string =>
  state.SimpleViewer?.dicomResource ?? '';

/**
 *  Returns all visits with labels
 * @param allConfigLabels  - all DICOM labels from configuration flat (merged with modalityName)
 * @param patientVisits  - all visits for patient
 * @param config  - context
 * @param qcVisits  - all visits with series
 * @returns {ViewerVisit[]} - all visits with labels
 */
const makeJoinedVisits = (
  allConfigLabels: LabelMerged[],
  patientVisits: QCTaskPatientVisit[],
  config: Context | undefined,
  qcVisits: QCVisitWithSeries[]
): ViewerVisit[] => {
  const configVisits =
    config?.cohorts.map(cohort => cohort.visits).flat() ?? [];

  return qcVisits.map(visit => {
    const { dicomSeries, visitDefinitionId, visitId, studyInstanceUid } = visit;

    const configVisit = configVisits.find(cv => cv.id === visitDefinitionId);

    const allLabels = getAllVisitLabels(
      allConfigLabels,
      configVisit?.labels ?? [],
      visitDefinitionId
    );

    const patientVisit = patientVisits.find(pv =>
      visitDefinitionId
        ? pv.visitDefinitionId === visitDefinitionId
        : pv.id === visitId
    );

    return {
      dicomSeries,
      visitId,
      studyInstanceUid,
      id: visitDefinitionId,
      visitDefinitionId,
      taskId: patientVisit?.taskId,
      allLabels,
      earlyTerminated: patientVisit?.earlyTerminated,
      type: patientVisit?.type,
      visitName: visitDefinitionId
        ? configVisit?.name
        : patientVisit?.visitName,
    } as ViewerVisit;
  });
};

export const prepareSimpleStudyState = (s: RootState): StudyType => {
  const { auth, clinicalTrialContext, clinicalTrialInventory } = s;
  console.log({ auth });
  const allConfigLabels = makeAllConfigLabelsFlat(
    clinicalTrialContext.data?.configuration.labelGroups ?? []
  );

  const istaskData = selectSimpleViewerData(s);
  const simpleViewerState = s.SimpleViewer;
  const overviewTasks: OverviewUpload[] = simpleViewerState.tasks ?? [];

  const visitCtx = istaskData?.visitContext[0]
    ? {
        ...(
          overviewTasks &&
          overviewTasks.find(
            t =>
              t.isTaskPageItemDto.visitId ===
              istaskData.visitContext[0]?.visitId
          )
        )?.isTaskPageItemDto,
        ...istaskData.visitContext[0],
      }
    : undefined;

  const patientVisits: QCTaskPatientVisit[] = overviewTasks
    .map(
      t =>
        ({
          id: t.isTaskPageItemDto.visitId,
          taskId: t.isTaskPageItemDto.id,
          visitDefinitionId: t.isTaskPageItemDto.visitDefinitionId,
          earlyTerminated: t.isTaskPageItemDto.visitEarlyTermination,
          type: t.isTaskPageItemDto.visitType,
          visitName: t.isTaskPageItemDto.visitName,
        }) as QCTaskPatientVisit
    )
    .filter(pv => pv.id);

  const qcVisits: QCVisitWithSeries[] =
    istaskData?.visitContext?.map((vc): QCVisitWithSeries => {
      const pv = patientVisits.find(pv => pv.id === vc.visitId);
      return {
        dicomSeries: vc.labeledSeries as unknown as DICOM_Series[],
        visitDefinitionId: pv?.visitDefinitionId as string,
        visitId: vc.visitId,
        studyInstanceUid: vc.studyInstanceUid,
      } as QCVisitWithSeries;
    }) ?? [];

  const visits: ViewerVisit[] = makeJoinedVisits(
    allConfigLabels,
    patientVisits,
    clinicalTrialContext.data?.configuration,
    qcVisits
  );

  return {
    clinicalTrialId: clinicalTrialContext.data?.configuration.id ?? '',
    clinicalTrialCode: clinicalTrialContext.data?.configuration.code ?? '',
    clinicalTrialName: clinicalTrialContext.data?.configuration.name ?? '',
    currentUserUid: auth.data?.['dynamika7/user_metadata'].uuid ?? '',
    DICOMResource: selectSimpleViewerDicomResource(s),
    task: {
      assigneeId: visitCtx?.assigneeId ?? '',
      id: visitCtx?.id ?? '',
      patientCode: istaskData?.patientCode,
      patientDateOfBirth: '',
      completed: false,
    },
    tokenUrl: '',
    visitId: visits?.[0]?.visitId,
    viewerMode: OHIFViewerModeType.SIMPLE_VIEWER,
    visits,
    instruments: clinicalTrialInventory.instruments,
    clinicalTrialInfo: {
      readingName: clinicalTrialContext.data?.configuration?.name ?? '',
      readingCode: istaskData?.patientCode ?? '',
    },
  };
};

export const isViewerAllowedToLoadForSimple = createSelector(
  [selectAuthRoles, selectSimpleViewerData],
  (roles, istaskData) => {
    const isPMSponsor =
      roles.includes('PROJECT_MANAGER') || roles.includes('SPONSOR');
    const hasVisits = !!istaskData?.visitContext?.length;
    const hasSeries = istaskData?.visitContext?.some(
      visit => !!visit?.labeledSeries?.length
    );
    return isPMSponsor && hasVisits && hasSeries;
  }
);
